import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { styled, alpha } from "@mui/material/styles";
import { Box, Typography, Paper, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAppSelector } from "../../../hooks/reduxHooks";

// ─── Styled components (kept from original) ───────────────────────────────────
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 7,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  width: "100%",
  [theme.breakpoints.up("sm")]: { width: "auto" },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  color: "#757FEF",
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  right: "0",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 5,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    backgroundColor: "#F5F7FA",
    borderRadius: "7px",
    padding: theme.spacing(1.4, 0, 1.4, 2),
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "100px",
      "&:focus": { width: "100px" },
    },
    [theme.breakpoints.up("md")]: {
      width: "198px",
      "&:focus": { width: "210px" },
    },
  },
}));

// ─── Build a flat list of searchable pages from the RBAC menu tree ────────────
function buildSearchIndex(menus) {
  const items = [];
  menus.forEach((menu) => {
    // Direct-link menus (no sub-pages)
    if (menu.route && (!menu.pages || menu.pages.length === 0)) {
      items.push({ label: menu.name, route: menu.route, parent: null, icon: menu.icon });
      return;
    }
    // Sub-pages
    (menu.pages || []).forEach((page) => {
      if (page.hide) return;
      items.push({ label: page.name, route: page.route, parent: menu.name, icon: page.icon || menu.icon });
    });
  });
  return items;
}

// ─── SearchForm ───────────────────────────────────────────────────────────────
const SearchForm = () => {
  const navigate   = useNavigate();
  const { menus }  = useAppSelector((s) => s.rbac);

  const [query,    setQuery]    = useState("");
  const [open,     setOpen]     = useState(false);
  const [active,   setActive]   = useState(0);
  const wrapperRef              = useRef(null);

  const index = useMemo(() => buildSearchIndex(menus), [menus]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return index.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.parent && item.parent.toLowerCase().includes(q))
    ).slice(0, 8);
  }, [query, index]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) => {
    setQuery(e.target.value);
    setActive(0);
    setOpen(true);
  };

  const goTo = (route) => {
    navigate(route);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((p) => (p + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((p) => (p - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) goTo(results[active].route);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && query.trim().length > 0;

  return (
    <form onSubmit={(e) => e.preventDefault()} ref={wrapperRef} style={{ position: "relative" }}>
      <Search className="search-form">
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>

        <StyledInputBase
          placeholder="Search pages…"
          inputProps={{ "aria-label": "search" }}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setOpen(true)}
          autoComplete="off"
        />
      </Search>

      {/* Dropdown */}
      {showDropdown && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: 260,
            maxWidth: 320,
            borderRadius: "10px",
            overflow: "hidden",
            zIndex: 9999,
            border: "1px solid #e2e8f0",
          }}
        >
          {results.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: "text.disabled" }}>No pages found</Typography>
            </Box>
          ) : (
            results.map((item, i) => (
              <Box
                key={item.route}
                onMouseDown={() => goTo(item.route)}
                onMouseEnter={() => setActive(i)}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: i === active ? "#f5f3ff" : "background.paper",
                  borderBottom: i < results.length - 1 ? "1px solid #f1f5f9" : "none",
                  "&:hover": { bgcolor: "#f5f3ff" },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: "6px",
                    bgcolor: i === active ? "#ede9fe" : "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <i className="material-symbols-outlined" style={{ fontSize: 16, color: i === active ? "#6366f1" : "#94a3b8" }}>
                    {item.icon || "article"}
                  </i>
                </Box>

                {/* Labels */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", lineHeight: 1.3 }}>
                    {item.label}
                  </Typography>
                  {item.parent && (
                    <Typography sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.2 }}>
                      {item.parent}
                    </Typography>
                  )}
                </Box>

                {/* Route hint */}
                <Typography sx={{ fontSize: 10, color: "#94a3b8", fontFamily: "monospace", ml: "auto", whiteSpace: "nowrap", flexShrink: 0 }}>
                  ↵
                </Typography>
              </Box>
            ))
          )}
        </Paper>
      )}
    </form>
  );
};

export default SearchForm;
